import React from 'react';
import { useWatch, Control } from 'react-hook-form';
import { formatPrice } from '../../../../shared/utils/formatPrice';
import { CreateBookingFormV4 } from '../../../../services/create-booking-v4/schema/CreateBookingSchemaV4';

// Tipe props untuk komponen ini
interface BookingSummaryPropsV4 {
    control: Control<CreateBookingFormV4>;
    totalExtras?: number;
}

// Komponen helper untuk setiap baris ringkasan
const SummaryRow: React.FC<{ label: string; value: string; className?: string }> = ({ label, value, className }) => (
    <div className={`d-flex justify-content-between align-items-center mb-1 ${className}`}>
        <span className="text-muted">{label}</span>
        <span className="fw-bold">{value}</span>
    </div>
);

const BookingSummaryV4: React.FC<BookingSummaryPropsV4> = ({ control, totalExtras = 0 }) => {
    // Gunakan useWatch untuk memantau perubahan pada seluruh array bookingroom
    const bookingRooms = useWatch({
        control,
        name: 'bookingroom',
    });

    // Kalkulasi total, akan berjalan setiap kali bookingRooms berubah
    const totals = React.useMemo(() => {
        let totalAccommodation = 0;
        let totalPayments = 0;

        if (Array.isArray(bookingRooms)) {
            bookingRooms.forEach(room => {
                // Kalkulasi total tarif untuk kamar ini
                const roomRateTotal = room.rates?.reduce((sum, rate) => sum + (Number(rate.rate) || 0), 0) ?? 0;
                totalAccommodation += roomRateTotal;

                // Kalkulasi total pembayaran untuk kamar ini
                const roomPaymentTotal = room.payments?.reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0) ?? 0;
                totalPayments += roomPaymentTotal;
            });
        }

        const grandTotalDue = totalAccommodation + totalExtras - totalPayments;

        return {
            totalAccommodation,
            totalPayments,
            grandTotalDue,
        };
    }, [bookingRooms, totalExtras]);

    return (
        <div className="booking-summary-v4 p-3 border-top bg-light">
            <h6 className="mb-1 fw-bold">Booking Summary</h6>
            <SummaryRow
                label="Total Accommodation"
                value={formatPrice(totals.totalAccommodation)}
            />
            {totalExtras > 0 && (
                <SummaryRow
                    label="Total Extras"
                    value={formatPrice(totalExtras)}
                />
            )}
            <SummaryRow
                label="Total Payments"
                value={`- ${formatPrice(totals.totalPayments)}`}
                className="text-success"
            />
            <hr className='my-2' />
            <SummaryRow
                label="Grand Total Due"
                value={formatPrice(totals.grandTotalDue)}
                className="fs-5"
            />
        </div>
    );
};

export default BookingSummaryV4;